import React, { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { FaCrosshairs, FaExpand } from 'react-icons/fa'

/**
 * Field map (2026-11-04): Leaflet with OpenStreetMap tiles. Pins, route lines and the "you are here" marker are drawn
 * by us, so the route still shows (on a blank background) when the tiles can't load — e.g. no signal in the field.
 *
 * Production note: OSM's public tile server is for light use; before going live point TILE_URL at a paid/own provider
 * (Mapbox, Stadia, Google...) and use a routing service for the road geometry (see geo.fetchRoadRoute).
 */
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTR = '&copy; OpenStreetMap contributors'

const pinHtml = (p) => {
    const cls = 'fo-pin fo-pin--' + (p.tone || 'blue') + (p.selected ? ' is-selected' : '') + (p.pulse ? ' is-pulse' : '') + (p.size ? ' fo-pin--' + p.size : '') + (p.dim ? ' is-dim' : '')
    const style = p.color ? ' style="background:' + p.color + '"' : ''
    return '<div class="' + cls + '"' + style + '><span>' + (p.text === undefined || p.text === null ? '' : String(p.text).replace(/</g, '&lt;')) + '</span></div>'
}

export default function FieldMap({ pins, dots, lines, me, depot, onPick, fitKey, focus, height, paddingBottom, children, legend, emptyHint }) {
    const hostRef = useRef(null)
    const mapRef = useRef(null)
    const layersRef = useRef({})
    const pickRef = useRef(onPick)
    const [tilesFailed, setTilesFailed] = useState(false)
    pickRef.current = onPick

    // create the map once
    useEffect(() => {
        const map = L.map(hostRef.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: true, tap: true }).setView([33.4, -85.4], 7)
        const tiles = L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTR })
        let errors = 0
        tiles.on('tileerror', () => { errors++; if (errors >= 3) setTilesFailed(true) })
        tiles.on('tileload', () => { if (errors > 0) { errors = 0; setTilesFailed(false) } })
        tiles.addTo(map)
        layersRef.current = { lines: L.layerGroup().addTo(map), dots: L.layerGroup().addTo(map), pins: L.layerGroup().addTo(map), me: L.layerGroup().addTo(map), depot: L.layerGroup().addTo(map) }
        mapRef.current = map
        const fix = () => map.invalidateSize()
        const timers = [setTimeout(fix, 80), setTimeout(fix, 500)]
        let ro = null
        if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(fix); ro.observe(hostRef.current) }
        window.addEventListener('resize', fix)
        return () => {
            timers.forEach(clearTimeout)
            if (ro) ro.disconnect()
            window.removeEventListener('resize', fix)
            map.remove()
            mapRef.current = null
        }
    }, [])

    // route lines
    useEffect(() => {
        const g = layersRef.current.lines
        if (!g) return
        g.clearLayers()
        ;(lines || []).forEach((l) => {
            if (!l.coords || l.coords.length < 2) return
            if (l.casing) L.polyline(l.coords, { color: '#FFFFFF', weight: (l.weight || 5) + 4, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }).addTo(g)
            L.polyline(l.coords, { color: l.color || '#2563EB', weight: l.weight || 5, opacity: l.opacity === undefined ? 0.9 : l.opacity, dashArray: l.dashed ? '8 10' : null, lineCap: 'round', lineJoin: 'round' }).addTo(g)
        })
    }, [lines])

    // numbered pins
    useEffect(() => {
        const g = layersRef.current.pins
        if (!g) return
        g.clearLayers()
        ;(pins || []).forEach((p) => {
            const size = p.size === 'lg' ? 46 : p.size === 'sm' ? 28 : 38
            const icon = L.divIcon({ className: 'fo-pin-wrap', html: pinHtml(p), iconSize: [size, size], iconAnchor: [size / 2, size / 2] })
            const m = L.marker([p.lat, p.lng], { icon: icon, title: p.title || '', zIndexOffset: p.selected ? 1000 : p.pulse ? 500 : 0, riseOnHover: true })
            m.on('click', () => { if (pickRef.current) pickRef.current(p.id, 'pin') })
            m.addTo(g)
        })
    }, [pins])

    // small suggestion dots
    useEffect(() => {
        const g = layersRef.current.dots
        if (!g) return
        g.clearLayers()
        ;(dots || []).forEach((p) => {
            const icon = L.divIcon({ className: 'fo-pin-wrap', html: '<div class="fo-dot' + (p.selected ? ' is-selected' : '') + '"><span>+</span></div>', iconSize: [26, 26], iconAnchor: [13, 13] })
            const m = L.marker([p.lat, p.lng], { icon: icon, title: p.title || '', zIndexOffset: p.selected ? 900 : 100 })
            m.on('click', () => { if (pickRef.current) pickRef.current(p.id, 'dot') })
            m.addTo(g)
        })
    }, [dots])

    // me + depot
    useEffect(() => {
        const g = layersRef.current.me
        if (!g) return
        g.clearLayers()
        if (me) L.marker([me.lat, me.lng], { icon: L.divIcon({ className: 'fo-pin-wrap', html: '<div class="fo-me"><i></i></div>', iconSize: [28, 28], iconAnchor: [14, 14] }), zIndexOffset: 1200, interactive: false }).addTo(g)
    }, [me])
    useEffect(() => {
        const g = layersRef.current.depot
        if (!g) return
        g.clearLayers()
        if (depot) L.marker([depot.lat, depot.lng], { icon: L.divIcon({ className: 'fo-pin-wrap', html: '<div class="fo-depot"><span>W</span></div>', iconSize: [34, 34], iconAnchor: [17, 17] }), title: depot.name || 'Warehouse' }).addTo(g)
    }, [depot])

    const points = () => {
        const pts = []
        ;(pins || []).forEach((p) => pts.push([p.lat, p.lng]))
        ;(dots || []).forEach((p) => pts.push([p.lat, p.lng]))
        if (me) pts.push([me.lat, me.lng])
        if (depot) pts.push([depot.lat, depot.lng])
        return pts
    }
    const fit = () => {
        const map = mapRef.current
        if (!map) return
        const pts = points()
        if (pts.length === 0) return
        map.invalidateSize()
        if (pts.length === 1) { map.setView(pts[0], 14); return }
        map.fitBounds(L.latLngBounds(pts), { paddingTopLeft: [36, 36], paddingBottomRight: [36, 36 + (paddingBottom || 0)], maxZoom: 15 })
    }

    // fit when the route changes (not on every pin refresh, so the user's own panning is respected)
    useEffect(() => {
        const t = setTimeout(fit, 120)
        return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fitKey])

    // fly to a requested place
    useEffect(() => {
        if (!focus || !mapRef.current) return
        mapRef.current.setView([focus.lat, focus.lng], focus.zoom || 15, { animate: true })
    }, [focus])

    const locate = () => {
        const map = mapRef.current
        if (map && me) map.setView([me.lat, me.lng], Math.max(map.getZoom(), 14), { animate: true })
    }

    return (
        <div className="fo-map" style={{ height: height || '100%' }}>
            <div ref={hostRef} className="fo-map__canvas"></div>
            <div className="fo-map__tools">
                <button type="button" onClick={fit} aria-label="Fit the whole route" title="Fit the whole route"><FaExpand /></button>
                {me ? <button type="button" onClick={locate} aria-label="Center on my position" title="Center on my position"><FaCrosshairs /></button> : null}
            </div>
            {tilesFailed ? <div className="fo-map__notice">Map background unavailable (no signal?). Your route and stops still work.</div> : null}
            {(pins || []).length === 0 && emptyHint ? <div className="fo-map__empty">{emptyHint}</div> : null}
            {legend ? (
                <div className="fo-map__legend" aria-label="Legend">
                    <span><i className="fo-lg fo-lg--blue"></i>To visit</span>
                    <span><i className="fo-lg fo-lg--cyan"></i>In progress</span>
                    <span><i className="fo-lg fo-lg--green"></i>Done</span>
                    <span><i className="fo-lg fo-lg--amber"></i>No order</span>
                    <span><i className="fo-lg fo-lg--red"></i>Missed</span>
                    <span><i className="fo-lg fo-lg--purple"></i>Added</span>
                </div>
            ) : null}
            {children}
        </div>
    )
}
